import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, ChartBar, DownloadSimple, Truck, Package, User } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * P6 · Owner daily summary — read-only rollup for Administrator/Owner roles.
 * Renders nothing when the caller isn't a manager (endpoint 403s).
 *
 * DSG-001 compliant.
 */
export default function OwnerSummaryWidget() {
  const { user } = useAuth();
  const isManager = user && ["Administrator", "Owner"].includes(user.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/owner-summary");
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isManager) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Section", "Label", "Count"]];
    for (const [k, v] of Object.entries(data.orders_by_status || {})) rows.push(["Status", k, v]);
    for (const r of data.orders_by_driver || []) rows.push(["Driver", r.label, r.count]);
    for (const r of data.orders_by_product || []) rows.push(["Product", r.label, r.count]);
    for (const r of data.orders_by_customer || []) rows.push(["Customer", r.label, r.count]);
    const ar = data.ar_tasks_completed || {};
    rows.push(["AR Turnaround", "Completed", ar.completed_count || 0]);
    rows.push(["AR Turnaround", "Avg (min)", ar.avg_turnaround_min || 0]);
    rows.push(["AR Turnaround", "Max (min)", ar.max_turnaround_min || 0]);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `owner-summary-${data.date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Summary exported");
  };

  if (!isManager || forbidden) return null;

  return (
    <div data-testid="owner-summary-widget" className="rounded-md border border-divider bg-surface">
      <div className="flex items-center justify-between border-b border-divider px-4 py-2.5">
        <div className="flex items-center gap-2">
          <ChartBar size={16} weight="regular" className="text-ink-secondary" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-secondary">
            Owner Summary · {data?.date || "today"}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="owner-summary-export"
            onClick={exportCsv}
            disabled={!data}
            className="inline-flex items-center gap-1 rounded-md border border-divider-strong px-2.5 py-1 text-xs font-medium text-ink-secondary hover:bg-surface-alt disabled:opacity-50"
          >
            <DownloadSimple size={12} weight="bold" /> CSV
          </button>
          <button
            data-testid="owner-summary-refresh"
            onClick={load}
            disabled={loading}
            className="rounded-md border border-divider-strong px-2 py-1 text-xs text-ink-secondary hover:bg-surface-alt disabled:opacity-50"
            aria-label="Refresh"
          >
            <ArrowsClockwise size={12} weight="bold" />
          </button>
        </div>
      </div>
      {loading || !data ? (
        <div className="p-4 text-sm text-ink-muted">{loading ? "Loading…" : "No data"}</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-4">
          <StatCard label="Dispatches today" value={data.orders_total} accent />
          <RankList title="By Driver" icon={Truck} items={data.orders_by_driver} />
          <RankList title="By Product" icon={Package} items={data.orders_by_product} />
          <RankList title="By Customer" icon={User} items={data.orders_by_customer} />
        </div>
      )}
      {data?.ar_tasks_completed && (
        <div className="border-t border-divider-subtle px-4 py-2.5 text-xs text-ink-secondary">
          <span className="font-semibold uppercase tracking-widest text-ink-muted">AR turnaround: </span>
          <span data-testid="owner-summary-ar-completed">{data.ar_tasks_completed.completed_count}</span> completed
          {" · avg "}
          <span data-testid="owner-summary-ar-avg" className="font-mono">{data.ar_tasks_completed.avg_turnaround_min} min</span>
          {" · max "}
          <span className="font-mono">{data.ar_tasks_completed.max_turnaround_min} min</span>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`rounded-md p-3 ${accent ? "bg-brand text-brand-foreground" : "bg-surface-alt"}`}>
      <div className={`text-[11px] font-semibold uppercase tracking-widest ${accent ? "text-brand-foreground/80" : "text-ink-muted"}`}>{label}</div>
      <div className={`mt-1 font-display text-3xl font-bold ${accent ? "text-brand-foreground" : "text-charcoal"}`}>{value}</div>
    </div>
  );
}

function RankList({ title, icon: Icon, items }) {
  const top = (items || []).slice(0, 5);
  return (
    <div className="rounded-md border border-divider p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-ink-muted">
        <Icon size={11} weight="bold" /> {title}
      </div>
      {top.length === 0 ? (
        <div className="text-xs text-ink-muted">—</div>
      ) : (
        <ul className="space-y-1 text-sm">
          {top.map((r) => (
            <li key={r.label} className="flex items-center justify-between gap-2">
              <span className="truncate text-ink">{r.label}</span>
              <span className="font-mono text-xs font-semibold text-ink-secondary">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
